import type { DayCategoryCompletion } from '@travel-planner/shared/schemas';
import type { LucideIcon } from 'lucide-react';
import { BedDouble, Car, Check, Compass, Utensils } from 'lucide-react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './category-tab-bar.module.scss';

export type GeneratorCategory =
  | 'transportation'
  | 'accommodation'
  | 'activities'
  | 'dining';

export type CategoryTabItem = {
  category: GeneratorCategory;
  label: string;
  status: DayCategoryCompletion;
  /** Pill text, e.g. 'Picked', '2/3', 'Missing'. */
  statusLabel: string;
};

// Pen `oomtC` tabs each carry a lucide category icon: Stay = bed-double,
// Activities = compass, Food = utensils, Transportation = car.
const CATEGORY_ICON: Record<GeneratorCategory, LucideIcon> = {
  accommodation: BedDouble,
  activities: Compass,
  dining: Utensils,
  transportation: Car,
};

const styles = {
  root: pickCss(css, css.root),
  tab: pickCss(css, css.tab),
  icon: pickCss(css, css.icon),
  label: pickCss(css, css.tabLabel),
  pill: pickCss(css, css.pill),
  indicator: pickCss(css, css.indicator),
};

type CategoryTabBarProps = {
  tabs: ReadonlyArray<CategoryTabItem>;
  activeCategory: GeneratorCategory;
  onSelect: (category: GeneratorCategory) => void;
};

/**
 * CategoryTabBar — pen component `oomtC` (tabs `BveqG`). Four category tabs
 * (Stay → Activities → Food → Transportation) with a lucide icon, label, and
 * per-day status pill (`Picked` / `2/3` / `Missing`). Active tab is
 * highlighted and carries the purple indicator bar.
 */
export const CategoryTabBar = ({
  tabs,
  activeCategory,
  onSelect,
}: CategoryTabBarProps) => (
  <div className={styles.root()} role="tablist">
    {tabs.map((tab) => {
      const isActive = tab.category === activeCategory;
      const Icon = CATEGORY_ICON[tab.category];
      return (
        <button
          aria-selected={isActive}
          className={joinCss(styles.tab({ isActive, status: tab.status }))}
          key={tab.category}
          onClick={() => onSelect(tab.category)}
          role="tab"
          type="button"
        >
          <Icon
            aria-hidden="true"
            className={styles.icon({ isActive, status: tab.status })}
            size={13}
          />
          <span className={styles.label()}>{tab.label}</span>
          <span className={joinCss(styles.pill({ status: tab.status }))}>
            {tab.status === 'complete' ? (
              <Check aria-hidden="true" size={8} />
            ) : (
              <span aria-hidden="true">!</span>
            )}
            {tab.statusLabel}
          </span>
          <span
            aria-hidden="true"
            className={joinCss(styles.indicator({ isActive }))}
          />
        </button>
      );
    })}
  </div>
);
